import { legacySelectStyle } from "@repo/ui/Select/select";
import { IconCheck } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { ListBox, ListBoxItem, Popover, Select } from "react-aria-components";

interface PlaybackRateSelectProps {
  playbackRate: number;
  onPlaybackRateChange: (value: number) => void;
  children?: ReactNode;
}

// Options are similar to Loom and Zoom
const options = [0.8, 1, 1.2, 1.5, 1.7, 2, 2.5].map((rate) => ({
  id: buildIdForRate(rate),
  label: buildLabelForRate(rate),
  value: rate,
}));

export const PlaybackRateSelect: FC<PlaybackRateSelectProps> = (props) => {
  return (
    <Select
      aria-label="Playback speed"
      selectedKey={findSelectedOptionId(props.playbackRate)}
      onSelectionChange={(key) => {
        const option = options.find((option) => option.id === key);
        if (option) {
          props.onPlaybackRateChange(option.value);
        }
      }}
    >
      {props.children}

      <Popover className={legacySelectStyle().popover()}>
        <ListBox items={options} disallowEmptySelection>
          {(option) => (
            <ListBoxItem
              id={option.id}
              textValue={option.label}
              className="group flex items-center gap-2 px-3 py-2 text-foreground select-none focus:bg-primary focus:text-primary-foreground disabled:cursor-not-allowed disabled:text-gray-400 selected:font-semibold"
            >
              <span className="flex-auto truncate">{option.label}</span>
              <div className="flex size-5 shrink-0 items-center justify-center">
                <IconCheck className="hidden size-full text-indigo-600 group-focus:text-white group-selected:block" />
                {option.value === 1 ? (
                  <div className="size-1 rounded-full bg-gray-600 group-focus:bg-white group-selected:hidden"></div>
                ) : null}
              </div>
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
};

function buildIdForRate(rate: number): string {
  return rate.toFixed(1);
}

export function buildLabelForRate(rate: number): string {
  return `${rate.toFixed(1).replace(/\.0$/, "")}x`;
}

function findSelectedOptionId(playbackRate: number) {
  // Find the option closest to the given playback rate
  const distances = options.map(({ value }) =>
    Math.abs(1 - playbackRate / value),
  );
  const minDistance = Math.min(...distances);
  const selectedIndex = distances.indexOf(minDistance);
  return options[selectedIndex]?.id ?? buildIdForRate(1);
}

function findOptionAtOffset(currentPlaybackRate: number, offset: number) {
  const currentId = findSelectedOptionId(currentPlaybackRate);
  const currentIndex = options.findIndex((option) => option.id === currentId);
  const newIndex = Math.min(
    Math.max(0, currentIndex + offset),
    options.length - 1,
  );
  return options[newIndex]?.value ?? currentPlaybackRate;
}

export function findNextPlaybackRate(currentPlaybackRate: number) {
  return findOptionAtOffset(currentPlaybackRate, 1);
}

export function findPreviousPlaybackRate(currentPlaybackRate: number) {
  return findOptionAtOffset(currentPlaybackRate, -1);
}
